<script setup lang="ts">
  // Ported from cartadigital-pos
  // app/components/pos/main/PosMainIncomingOrderCard.vue, fed by admin IOrder
  // (socket-fed ordersStore.newOrders) instead of the -pos incoming store.
  import { computed, getCurrentInstance } from 'vue';
  import { OrderType } from '@/enums/OrderEnums';
  import { IOrder } from '@/interfaces/IOrder';
  import {
    deriveOrderDiscountBadge,
    formatDeliveryAddressLine,
    getEffectivePaymentStatusInfo,
  } from '@/domain/pos/order-formatting';
  import {
    getPosOrderTypeIcon,
    getPosOrderTypeLabel,
  } from '@/domain/pos/order-type-ui';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { orderItemsToText } from '@/utils/orders.utils';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  const props = defineProps<{
    order: IOrder;
    unseen?: boolean;
    acceptLoading?: boolean;
    rejectLoading?: boolean;
    acceptDisabled?: boolean;
    rejectDisabled?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'view', order: IOrder): void;
    (e: 'accept', order: IOrder): void;
    (e: 'reject', order: IOrder): void;
    (e: 'seen', order: IOrder): void;
  }>();

  const money = usePosMoney();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const orderType = computed(() => props.order.type as OrderType);
  const orderTypeIcon = computed(() => getPosOrderTypeIcon(orderType.value));
  const orderTypeLabel = computed(() => getPosOrderTypeLabel(orderType.value));

  const headerTitle = computed(() => {
    const name = String(props.order.userName ?? '').trim();
    if (name) return name;

    const tel = String(props.order.userTel ?? '').trim();
    if (tel) return tel;

    const humanId = String(props.order.humanId ?? '').trim();
    if (humanId) return i18nT('posV2.orderNumber', { id: humanId });

    return i18nT('posV2.orderNumber', { id: props.order.id });
  });

  const timeText = computed(() => {
    const date = new Date(props.order.createdAt);
    if (!Number.isFinite(date.getTime())) return '';
    return date.toLocaleTimeString(proxy.$i18n.locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  });

  const tableLine = computed(() => {
    if (orderType.value !== OrderType.Seat) return '';
    const name = String(props.order.table?.name ?? '').trim();
    return name ? i18nT('posV2.tableWithName', { name }) : '';
  });

  const addressLine = computed(() => {
    if (orderType.value !== OrderType.Delivery) return '';
    return formatDeliveryAddressLine({
      deliveryAddress: props.order.userAddress ?? null,
      deliveryAddress2: props.order.userAddress2 ?? null,
    });
  });

  const itemsPreview = computed(() =>
    orderItemsToText({ order: props.order, maxItems: 2 })
  );

  // Derives "Parcial" for partially-paid orders (club-balance debits).
  const paymentStatusInfo = computed(() =>
    getEffectivePaymentStatusInfo(props.order)
  );

  // Incoming cards take a raw IOrder (no orderToListItem pass) — incoming
  // online orders are exactly where coupons arrive, so derive here too.
  const discountBadgeText = computed(() => {
    const badge = deriveOrderDiscountBadge(props.order);
    if (!badge) return null;
    return badge.kind === 'percent'
      ? `-${badge.value}%`
      : `-${money.formatComputedMoney(badge.value)}`;
  });

  const isMutating = computed(
    () => !!(props.acceptLoading || props.rejectLoading)
  );

  /**
   * Keyboard activation only when the card itself is focused — a keydown
   * bubbling from the inner action buttons must keep its native behavior.
   */
  function handleKeyActivate(ev: KeyboardEvent) {
    if (ev.target !== ev.currentTarget) return;
    ev.preventDefault();
    emit('view', props.order);
  }
</script>

<template>
  <div
    class="pos-v2-incoming-card"
    :class="{ 'is-unseen': unseen }"
    role="button"
    tabindex="0"
    @click="emit('view', order)"
    @keydown.enter="handleKeyActivate"
    @keydown.space="handleKeyActivate"
    @pointerenter="emit('seen', order)"
  >
    <div class="card-header">
      <div class="header-left">
        <PosUiIcon :name="orderTypeIcon" class="type-icon" />
        <div class="title-wrap">
          <div class="title">{{ headerTitle }}</div>
          <div class="subtitle">{{ orderTypeLabel }}</div>
        </div>
      </div>

      <div class="header-right">
        <span v-if="unseen" class="unseen-dot" aria-hidden="true" />
        <span class="order-time">{{ timeText }}</span>
      </div>
    </div>

    <div class="card-body">
      <div v-if="tableLine" class="info-row">
        <PosUiIcon name="table-furniture" class="info-icon" />
        <span class="info-text">{{ tableLine }}</span>
      </div>

      <div v-if="addressLine" class="info-row">
        <PosUiIcon name="map-marker" class="info-icon" />
        <span class="info-text">{{ addressLine }}</span>
      </div>

      <div v-if="itemsPreview" class="info-row">
        <PosUiIcon name="cart" class="info-icon" />
        <span class="info-text">{{ itemsPreview }}</span>
      </div>

      <div v-if="order.comment" class="info-row comment">
        <PosUiIcon name="comment-text-outline" class="info-icon" />
        <span class="info-text">{{ order.comment }}</span>
      </div>
    </div>

    <div class="card-footer">
      <div class="footer-left">
        <div class="footer-badges">
          <PosUiBadge
            :color="paymentStatusInfo.color"
            variant="soft"
            size="xs"
            icon="wallet"
            class="payment-badge"
          >
            {{ $t(paymentStatusInfo.i18nKey) }}
          </PosUiBadge>
          <!-- Sky/info: informational role (emerald would read as "paid"). -->
          <PosUiBadge
            v-if="discountBadgeText"
            color="info"
            variant="soft"
            size="xs"
            icon="tag-outline"
            class="discount-badge"
          >
            {{ discountBadgeText }}
          </PosUiBadge>
        </div>
        <span class="total-value">
          {{ money.formatComputedMoney(order.total) }}
        </span>
      </div>

      <div class="footer-actions">
        <PosUiButton
          class="action-btn"
          color="success"
          variant="solid"
          size="lg"
          icon="check"
          :loading="acceptLoading"
          :disabled="acceptDisabled || isMutating"
          @click.stop="emit('accept', order)"
        >
          {{ $t('posV2.accept') }}
        </PosUiButton>

        <PosUiButton
          class="action-btn"
          color="error"
          variant="soft"
          size="lg"
          icon="close"
          :loading="rejectLoading"
          :disabled="rejectDisabled || isMutating"
          @click.stop="emit('reject', order)"
        >
          {{ $t('posV2.reject') }}
        </PosUiButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @keyframes unseen-card-bg-pulse {
    0%,
    100% {
      opacity: 0;
    }

    50% {
      opacity: 1;
    }
  }

  .pos-v2-incoming-card {
    @apply tw-relative tw-flex tw-cursor-pointer tw-flex-col tw-gap-2 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-p-3 tw-transition-shadow;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      opacity: 0;
      pointer-events: none;
    }

    // Incoming = attention: red pulse, not brand orange.
    &.is-unseen {
      @apply tw-border-red-300;

      &::before {
        @apply tw-bg-red-500/10;
        animation: unseen-card-bg-pulse 1.8s ease-in-out infinite;
      }
    }

    .card-header {
      @apply tw-flex tw-items-start tw-justify-between tw-gap-3;

      .header-left {
        @apply tw-flex tw-min-w-0 tw-items-start tw-gap-2;

        .type-icon {
          @apply tw-mt-0.5 tw-flex-none tw-text-lg tw-text-neutral-700;
        }

        .title-wrap {
          @apply tw-flex tw-min-w-0 tw-flex-col;

          .title {
            @apply tw-truncate tw-text-sm tw-font-semibold tw-text-neutral-900;
          }

          .subtitle {
            @apply tw-text-xs tw-text-neutral-500;
          }
        }
      }

      .header-right {
        @apply tw-flex tw-flex-none tw-items-center tw-gap-2;

        .unseen-dot {
          @apply tw-h-2.5 tw-w-2.5 tw-animate-pulse tw-rounded-full tw-bg-red-500;
        }

        .order-time {
          @apply tw-text-xs tw-text-neutral-400;
        }
      }
    }

    .card-body {
      @apply tw-flex tw-flex-col tw-gap-1;

      .info-row {
        @apply tw-flex tw-items-start tw-gap-2;

        .info-icon {
          @apply tw-mt-0.5 tw-flex-none tw-text-sm tw-text-neutral-400;
        }

        .info-text {
          @apply tw-line-clamp-2 tw-text-xs tw-text-neutral-600;
        }

        &.comment .info-text {
          @apply tw-italic tw-text-neutral-500;
        }
      }
    }

    .card-footer {
      @apply tw-flex tw-flex-col tw-gap-2 tw-pt-1;

      .footer-left {
        @apply tw-flex tw-items-center tw-justify-between tw-gap-2;

        .footer-badges {
          @apply tw-flex tw-items-center tw-gap-1.5;
        }

        .total-value {
          @apply tw-text-sm tw-font-semibold tw-tabular-nums tw-text-neutral-900;
        }
      }

      .footer-actions {
        @apply tw-grid tw-grid-cols-2 tw-gap-2;

        .action-btn {
          @apply tw-w-full tw-justify-center;
        }
      }
    }
  }
</style>
